<template>
  <div class="onboarding">
    <!-- Header -->
    <div class="onboarding__header">
      <div class="onboarding__logo">
        <img
          :src="myDesskBlack"
          alt="mydessk"
          style="height: 28px; width: auto"
        />
      </div>
      <a href="#" class="onboarding__skip-all" @click.prevent="skipAll">
        Omitir todo
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path
            d="M6 12l4-4-4-4"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </a>
    </div>

    <!-- Progress Steps -->
    <div class="onboarding__progress">
      <div
        v-for="s in 3"
        :key="s"
        class="onboarding__progress-dot"
        :class="{
          'onboarding__progress-dot--active': currentStep === s,
          'onboarding__progress-dot--completed': currentStep > s
        }"
      >
        <span v-if="currentStep > s" class="onboarding__progress-check">
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path
              d="M2.5 6l2.5 2.5 4.5-5"
              stroke="#fff"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </span>
        <span v-else>{{ s }}</span>
      </div>
      <div class="onboarding__progress-line">
        <div
          class="onboarding__progress-line-fill"
          :style="{ width: ((currentStep - 1) / 2) * 100 + '%' }"
        ></div>
      </div>
    </div>

    <!-- Step Content -->
    <div class="onboarding__content">
      <transition name="onboarding-fade" mode="out-in">
        <!-- ==================== STEP 1: Tu negocio ==================== -->
        <div v-if="currentStep === 1" key="step1" class="onboarding__step">
          <h2 class="onboarding__title">!Bienvenido a Mydessk!</h2>
          <p class="onboarding__subtitle">
            Cuentanos sobre tu negocio para personalizar tu experiencia
          </p>

          <el-form :model="form" ref="formStep1" label-position="top">
            <el-form-item label="Nombre del negocio" prop="business_name">
              <el-input
                v-model="form.business_name"
                placeholder="Ej: Mi Tienda Online"
              ></el-input>
            </el-form-item>

            <el-form-item label="Categoria de negocio" prop="category_id">
              <el-select
                v-model="form.category_id"
                placeholder="Selecciona una categoria"
                filterable
                style="width: 100%"
              >
                <el-option
                  v-for="cat in categories"
                  :key="cat.id"
                  :label="cat.name"
                  :value="cat.id"
                ></el-option>
              </el-select>
            </el-form-item>

            <div class="onboarding__field-label">Tamano de tu equipo</div>
            <div class="onboarding__team-sizes">
              <div
                v-for="size in teamSizes"
                :key="size.value"
                class="onboarding__team-card"
                :class="{
                  'onboarding__team-card--active':
                    form.people_number === size.value
                }"
                @click="form.people_number = size.value"
              >
                <span
                  class="onboarding__team-card-icon"
                  v-html="size.icon"
                ></span>
                <span class="onboarding__team-card-label">{{
                  size.label
                }}</span>
              </div>
            </div>
          </el-form>

          <a
            href="#"
            class="onboarding__skip-step"
            @click.prevent="nextStep(true)"
          >
            Omitir paso
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path
                d="M5 10l3.5-3.5L5 3"
                stroke="currentColor"
                stroke-width="1.3"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </a>
        </div>

        <!-- ==================== STEP 2: Contacto ==================== -->
        <div v-if="currentStep === 2" key="step2" class="onboarding__step">
          <h2 class="onboarding__title">Como te contactan tus clientes?</h2>
          <p class="onboarding__subtitle">
            Esta informacion aparecera en tus documentos y perfil publico
          </p>

          <el-form :model="form" ref="formStep2" label-position="top">
            <el-form-item label="Telefono del negocio" prop="business_phone">
              <el-input
                v-model="form.business_phone"
                placeholder="Ej: 0991234567"
              ></el-input>
            </el-form-item>

            <el-form-item label="Email del negocio" prop="business_email">
              <el-input
                v-model="form.business_email"
                placeholder="contacto@tunegocio.com"
              ></el-input>
            </el-form-item>

            <el-form-item label="Ciudad" prop="city_id">
              <el-select
                v-model="form.city_id"
                placeholder="Selecciona tu ciudad"
                filterable
                style="width: 100%"
              >
                <el-option
                  v-for="city in cities"
                  :key="city.id"
                  :label="city.name"
                  :value="city.id"
                ></el-option>
              </el-select>
            </el-form-item>
          </el-form>

          <a
            href="#"
            class="onboarding__skip-step"
            @click.prevent="nextStep(true)"
          >
            Omitir paso
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path
                d="M5 10l3.5-3.5L5 3"
                stroke="currentColor"
                stroke-width="1.3"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </a>
        </div>

        <!-- ==================== STEP 3: Personalizacion ==================== -->
        <div v-if="currentStep === 3" key="step3" class="onboarding__step">
          <h2 class="onboarding__title">Personaliza tu espacio</h2>
          <p class="onboarding__subtitle">
            Agrega tu marca para que tus documentos se vean profesionales
          </p>

          <el-form :model="form" ref="formStep3" label-position="top">
            <!-- Logo Upload -->
            <div class="onboarding__field-label">Logo de tu negocio</div>
            <div
              class="onboarding__upload-area"
              :class="{ 'onboarding__upload-area--has-preview': logoPreview }"
              @click="triggerFileInput"
              @dragover.prevent="onDragOver"
              @dragleave.prevent="onDragLeave"
              @drop.prevent="onFileDrop"
              :data-dragging="isDragging"
            >
              <input
                ref="fileInput"
                type="file"
                accept="image/png,image/jpeg,image/svg+xml"
                style="display: none"
                @change="onFileSelect"
              />
              <template v-if="logoPreview">
                <img
                  :src="logoPreview"
                  alt="Logo preview"
                  class="onboarding__upload-preview"
                />
                <button
                  class="onboarding__upload-change"
                  @click.stop="triggerFileInput"
                >
                  Cambiar
                </button>
              </template>
              <template v-else>
                <div class="onboarding__upload-icon">
                  <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                    <rect
                      x="4"
                      y="4"
                      width="32"
                      height="32"
                      rx="8"
                      stroke="#d1d5db"
                      stroke-width="1.5"
                      stroke-dasharray="4 3"
                    />
                    <path
                      d="M20 14v12M14 20h12"
                      stroke="#9ca3af"
                      stroke-width="1.5"
                      stroke-linecap="round"
                    />
                  </svg>
                </div>
                <p class="onboarding__upload-text">
                  Arrastra tu logo aqui o <span>haz clic para seleccionar</span>
                </p>
                <p class="onboarding__upload-hint">PNG, JPG o SVG. Max 2MB</p>
              </template>
            </div>

            <el-form-item
              label="RUC / Identificacion fiscal"
              prop="business_ruc"
              style="margin-top: 20px"
            >
              <el-input
                v-model="form.business_ruc"
                placeholder="Ej: 0991234567001"
              ></el-input>
            </el-form-item>
          </el-form>

          <p class="onboarding__note">
            Podras cambiar esto despues en Configuracion
          </p>
        </div>
      </transition>
    </div>

    <!-- Navigation Buttons -->
    <div class="onboarding__nav">
      <button
        v-if="currentStep > 1"
        class="onboarding__btn onboarding__btn--secondary"
        @click="prevStep"
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path
            d="M10 12L6 8l4-4"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
        Anterior
      </button>
      <div v-else></div>

      <button
        v-if="currentStep < 3"
        class="onboarding__btn onboarding__btn--primary"
        @click="nextStep(false)"
        :disabled="saving"
      >
        {{ saving ? "Guardando..." : "Siguiente" }}
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path
            d="M6 12l4-4-4-4"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>
      <button
        v-else
        class="onboarding__btn onboarding__btn--primary onboarding__btn--finish"
        @click="finishOnboarding"
        :disabled="saving"
      >
        {{ saving ? "Guardando..." : "Ir a mi dashboard" }}
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path
            d="M6 12l4-4-4-4"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>
    </div>
  </div>
</template>

<script>
import { myDesskBlack } from "@/services/resources";
import AuthServices from "../../services/AuthServices";
import CategoriesServices from "@/services/CategoriesServices";
import CitiesServices from "@/services/CitiesServices";

export default {
  name: "OnboardingPage",
  data() {
    return {
      myDesskBlack,
      currentStep: 1,
      saving: false,
      isDragging: false,
      logoFile: null,
      logoPreview: null,
      categories: [],
      cities: [],
      teamSizes: [
        {
          value: 1,
          label: "Solo yo",
          icon:
            '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="8" r="4"/><path d="M6 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2"/></svg>'
        },
        {
          value: 2,
          label: "2-5",
          icon:
            '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="9" cy="7" r="3.5"/><circle cx="17" cy="7" r="2.5"/><path d="M3 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2"/><path d="M21 21v-1.5a3 3 0 0 0-2-2.83"/></svg>'
        },
        {
          value: 4,
          label: "6-15",
          icon:
            '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="5" r="3"/><circle cx="5" cy="8" r="2.5"/><circle cx="19" cy="8" r="2.5"/><path d="M7 21v-1a5 5 0 0 1 10 0v1"/><path d="M2 21v-1a3 3 0 0 1 3-3"/><path d="M22 21v-1a3 3 0 0 0-3-3"/></svg>'
        },
        {
          value: 5,
          label: "16-50",
          icon:
            '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="4" y="3" width="16" height="18" rx="2"/><path d="M9 7h2"/><path d="M13 7h2"/><path d="M9 11h2"/><path d="M13 11h2"/><path d="M9 15h6"/><path d="M10 21v-3h4v3"/></svg>'
        },
        {
          value: 6,
          label: "50+",
          icon:
            '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M2 12h20"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10A15.3 15.3 0 0 1 12 2z"/></svg>'
        }
      ],
      form: {
        business_name: "",
        category_id: "",
        people_number: null,
        business_phone: "",
        business_email: "",
        city_id: "",
        business_ruc: ""
      }
    };
  },
  computed: {
    user() {
      return this.$store.getters.getUser || {};
    }
  },
  methods: {
    // ==================== Google Token Handling ====================
    async handleGoogleToken() {
      const urlParams = new URLSearchParams(window.location.search);
      const googleToken = urlParams.get("google_token");
      const error = urlParams.get("error");

      if (error === "google_cancelled") {
        this.$notifications.error({
          message: "Inicio de sesion con Google cancelado"
        });
        window.history.replaceState({}, document.title, "/onboarding");
        return;
      }

      if (googleToken) {
        this.$store.commit("setUser", { token: googleToken });
        try {
          const response = await AuthServices.getProfile();
          if (response.data && response.data.success) {
            const { user } = response.data;
            this.$store.commit("setUser", { ...user, token: googleToken });
          }
        } catch (e) {
          // Profile fetch failed, continue with token only
        }
        // Clean URL params
        window.history.replaceState({}, document.title, "/onboarding");
      }
    },

    // ==================== Load Data ====================
    loadUserData() {
      const user = this.user;
      if (user.user_business) {
        this.form.business_name =
          user.user_business.business_name || user.name || "";
        this.form.category_id = user.user_business.category_id || "";
        this.form.people_number = user.user_business.people_number || null;
        this.form.business_phone = user.user_business.business_phone || "";
        this.form.business_email =
          user.user_business.business_email || user.email || "";
        this.form.city_id = user.user_business.city_id || "";
        this.form.business_ruc = user.user_business.business_ruc || "";
      } else {
        this.form.business_name = user.name || "";
        this.form.business_email = user.email || "";
      }
    },

    async loadCategories() {
      try {
        const response = await CategoriesServices.getCategories();
        const data = response.data;
        if (data && data.categories) {
          this.categories = data.categories;
        } else if (data && data.data) {
          this.categories = data.data;
        } else if (data && Array.isArray(data)) {
          this.categories = data;
        }
      } catch (e) {
        // Silently fail
      }
    },

    async loadCities() {
      try {
        const response = await CitiesServices.getCities();
        const data = response.data;
        if (data && data.cities) {
          this.cities = data.cities;
        } else if (data && data.data) {
          this.cities = data.data;
        } else if (data && Array.isArray(data)) {
          this.cities = data;
        }
      } catch (e) {
        // Silently fail
      }
    },

    // ==================== File Upload ====================
    triggerFileInput() {
      this.$refs.fileInput.click();
    },

    onFileSelect(event) {
      const file = event.target.files[0];
      if (file) this.processFile(file);
    },

    onDragOver() {
      this.isDragging = true;
    },

    onDragLeave() {
      this.isDragging = false;
    },

    onFileDrop(event) {
      this.isDragging = false;
      const file = event.dataTransfer.files[0];
      if (file) this.processFile(file);
    },

    processFile(file) {
      const validTypes = ["image/png", "image/jpeg", "image/svg+xml"];
      if (!validTypes.includes(file.type)) {
        this.$notifications.error({
          message: "Solo se permiten archivos PNG, JPG o SVG"
        });
        return;
      }
      if (file.size > 2 * 1024 * 1024) {
        this.$notifications.error({
          message: "El archivo no puede superar los 2MB"
        });
        return;
      }
      this.logoFile = file;
      const reader = new FileReader();
      reader.onload = e => {
        this.logoPreview = e.target.result;
      };
      reader.readAsDataURL(file);
    },

    // ==================== Navigation ====================
    async nextStep(skip) {
      if (!skip) {
        this.saving = true;
        try {
          await this.saveCurrentStep();
        } catch (e) {
          this.$notifications.error({
            message: "Hubo un error al guardar. Intenta de nuevo."
          });
          this.saving = false;
          return;
        }
        this.saving = false;
      }
      if (this.currentStep < 3) {
        this.currentStep++;
      }
    },

    prevStep() {
      if (this.currentStep > 1) {
        this.currentStep--;
      }
    },

    async saveCurrentStep() {
      if (this.currentStep === 1) {
        await AuthServices.updateOnboardingBusiness({
          business_name: this.form.business_name,
          category_id: this.form.category_id,
          people_number: this.form.people_number
        });
      } else if (this.currentStep === 2) {
        await AuthServices.updateOnboardingContact({
          business_phone: this.form.business_phone,
          business_email: this.form.business_email,
          city_id: this.form.city_id
        });
      } else if (this.currentStep === 3) {
        const formData = new FormData();
        if (this.logoFile) {
          formData.append("business_logo", this.logoFile);
        }
        if (this.form.business_ruc) {
          formData.append("business_ruc", this.form.business_ruc);
        }
        // Only send if there's actual data to save
        if (this.logoFile || this.form.business_ruc) {
          await AuthServices.updateOnboardingBranding(formData);
        }
      }
    },

    async finishOnboarding() {
      this.saving = true;

      // All steps are best-effort — server errors don't block navigation
      try {
        await this.saveCurrentStep();
      } catch (e) {
        // Non-blocking: branding/contact save failure shouldn't stop the user
      }

      try {
        await AuthServices.completeOnboarding();
      } catch (e) {
        // Non-blocking: proceed to dashboard regardless
      }

      try {
        const response = await AuthServices.getProfile();
        if (response.data && response.data.success) {
          const { user } = response.data;
          this.$store.commit("setUser", { ...this.user, ...user });
        }
      } catch (e) {
        // Non-blocking
      }

      this.$router.push({ name: "dashboard" });
      this.saving = false;
    },

    async skipAll() {
      this.saving = true;
      try {
        await AuthServices.completeOnboarding();

        try {
          const response = await AuthServices.getProfile();
          if (response.data && response.data.success) {
            const { user } = response.data;
            this.$store.commit("setUser", {
              ...this.user,
              ...user
            });
          }
        } catch (e) {
          // Continue anyway
        }

        this.$router.push({ name: "dashboard" });
      } catch (e) {
        this.$notifications.error({
          message: "Hubo un error. Intenta de nuevo."
        });
      }
      this.saving = false;
    }
  },

  mounted() {
    this.handleGoogleToken();
    this.loadUserData();
    this.loadCategories();
    this.loadCities();
  }
};
</script>

<style scoped lang="scss">
@import "./Onboarding.scss";
</style>

<style lang="scss">
/* Element UI overrides for Onboarding - must be unscoped to penetrate child components */
.onboarding .el-form-item__label {
  font-size: 13px !important;
  font-weight: 700 !important;
  color: #1e1c42 !important;
  padding-bottom: 4px !important;
  line-height: 1.4 !important;
  font-family: "Plus Jakarta Sans", sans-serif !important;
}

.onboarding .el-input__inner {
  border: 1.5px solid #e5e7eb !important;
  border-radius: 10px !important;
  font-family: "Plus Jakarta Sans", sans-serif !important;
  color: #374151 !important;
  height: 50px !important;
  padding: 0 16px !important;
  font-size: 14px !important;
}

.onboarding .el-input__inner:focus {
  border-color: #3ecfb4 !important;
}

.onboarding .el-select .el-input__inner {
  border: 1.5px solid #e5e7eb !important;
  border-radius: 10px !important;
  height: 50px !important;
  padding: 0 16px !important;
}
</style>
